import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useLang } from "@/i18n";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG = "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=2000&q=80";

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  const ease = [0.22, 1, 0.36, 1];
  const statIds = ["hero-stat-team", "hero-stat-year", "hero-stat-response", "hero-stat-availability"];

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden bg-[#0E1B2A] hero-grain">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src="/images/hero.jpg" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = HERO_IMG; }} alt="Villa de luxe sur la Côte d'Azur" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0E1B2A] via-[#0E1B2A]/55 to-[#0E1B2A]/25" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0E1B2A]/70 via-transparent to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-end px-5 pb-14 pt-32 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease }}
          className="mb-6 text-xs font-semibold uppercase tracking-[0.3em] text-[#D4AF37]"
        >
          {t.hero.overline}
        </motion.p>

        <h1 data-testid="hero-heading" className="max-w-4xl font-serif text-4xl sm:text-6xl lg:text-7xl font-medium leading-[1.06] tracking-tight text-[#FAF8F5] text-balance">
          {t.hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1 -mb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.25 + i * 0.14, ease }}
              >
                {i === 1 ? <span className="italic text-[#E8C9B0]">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease }}
          className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#FAF8F5]/75"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-quote-cta"
            onClick={() => scrollToId("contact")}
            className="group inline-flex items-center gap-2 rounded-full bg-[#C87A57] px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#b06a48] hover:shadow-[0_20px_40px_-10px_rgba(200,122,87,0.5)] hover:-translate-y-0.5"
          >
            {t.hero.ctaQuote}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-services-cta"
            onClick={() => scrollToId("services")}
            className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/10 hover:border-white/70"
          >
            {t.hero.ctaServices}
            <ArrowDown size={16} />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.15 }}
          className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-medium uppercase tracking-widest text-[#FAF8F5]/60"
        >
          <span data-testid="hero-stat-siret">{t.hero.siret}</span>
          <span className="hidden sm:inline text-[#D4AF37]">·</span>
          <span data-testid="hero-stat-partners-note">{t.hero.partners}</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.3, ease }}
          className="mt-12 grid grid-cols-2 gap-6 border-t border-white/15 pt-8 sm:grid-cols-4"
        >
          {t.hero.stats.map((s, i) => (
            <div key={i} data-testid={statIds[i]}>
              <div className="font-serif text-3xl sm:text-4xl font-semibold text-[#FAF8F5]">{s.v}</div>
              <div className="mt-1 text-xs uppercase tracking-[0.2em] text-[#FAF8F5]/55">{s.l}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
